import React, { useState, useEffect, createContext, useContext } from 'react';
import { Card, Spinner, Text } from '@shopify/polaris';
import { authService } from '../services/authService';
import LoginPage from './LoginPage';

// Create authentication context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component that handles all authentication logic
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [user, setUser] = useState(null);

  // Initialize authentication on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        authService.initializeAuth();
        const isValid = await authService.validateToken();
        
        if (isValid) {
          setIsAuthenticated(true);
          setUser(authService.getUser());
        } else {
          // Clear invalid token
          authService.logout();
        }
      } catch (error) {
        console.error('Authentication initialization failed');
        authService.logout();
      } finally {
        setIsLoadingAuth(false);
      }
    };

    initializeAuth();
  }, []);

  // Handle successful login
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setUser(authService.getUser());
  };

  // Handle logout
  const handleLogout = (toastCallback) => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    
    // Call toast callback if provided
    if (toastCallback) {
      toastCallback('Successfully logged out!');
    }
  };

  // Loading state while checking authentication
  if (isLoadingAuth) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Card>
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <Spinner size="large" />
            <Text variant="bodyMd" as="p" alignment="center" style={{ marginTop: '16px' }}>
              Loading...
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  // Show login page when not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Provide auth context to children when authenticated
  const authValue = {
    isAuthenticated,
    user,
    handleLogout,
    handleLoginSuccess
  };

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; 